import React, { useEffect, useState } from 'react';
import { useI18n } from '../i18n/useI18n';
import { Plus, SlidersHorizontal } from 'lucide-react';
import { peecAPI } from '../api/peecAPI';
import { Prompt, PromptTopic } from '../types';
import { Badge, Delta, FilterBar, FilterConfig, IconTool, MiniTabs, PageHeader, SearchToolbar, SectionCard, TableFrame, Td, Th, ToolButton, ToolbarIcons } from '../components/PeecUI';

interface PromptsPageProps {
  workspaceId: string;
}

const PromptsPage: React.FC<PromptsPageProps> = ({ workspaceId }) => {
  const { t } = useI18n();
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [topics, setTopics] = useState<PromptTopic[]>([]);
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  const [activeTopic, setActiveTopic] = useState('All topics');
  const [timeRange, setTimeRange] = useState('全部时间');
  const [locationFilter, setLocationFilter] = useState('全部地区');
  const [webSearchFilter, setWebSearchFilter] = useState('全部');
  const tabItems = [t('pages.active'), t('pages.suggested'), t('pages.archived')];

  useEffect(() => {
    peecAPI.getPrompts(workspaceId).then((data) => {
      setPrompts(data?.data ?? []);
      setTopics(data?.topics ?? []);
    }).catch((error) => console.error('Failed to fetch prompts:', error));
  }, [workspaceId]);

  const visiblePrompts = prompts
    .filter((p) => activeTopic === 'All topics' || p.topic === activeTopic)
    .filter((p) => locationFilter === '全部地区' || p.location === locationFilter)
    .filter((p) => webSearchFilter === '全部' || (webSearchFilter === '开启' ? p.web_search : !p.web_search));

  const promptFilters: FilterConfig[] = [
    { key: 'time', label: '全部时间', options: ['全部时间', '过去7天', '过去30天', '过去90天'], value: timeRange, onChange: setTimeRange },
    { key: 'location', label: '全部地区', options: ['全部地区', 'CN', 'US', 'GLOBAL'], value: locationFilter, onChange: setLocationFilter },
    { key: 'webSearch', label: '全部', options: ['全部', '开启', '关闭'], value: webSearchFilter, onChange: setWebSearchFilter },
  ];

  return (
    <main className="flex h-screen w-full flex-col overflow-hidden text-zinc-950">
      <div className="flex min-h-0 flex-1 flex-col px-5 py-7 sm:px-8">
        <PageHeader title={t('sidebar.prompts')} />
        <FilterBar filters={promptFilters} />

        <div className="min-h-0 flex-1 grid gap-5 xl:grid-cols-[280px_1fr]">
          <aside className="flex min-h-0 flex-col rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <h2 className="font-semibold">{t('pages.topics')}</h2>
              <IconTool label={t('pages.sortTopics')}><SlidersHorizontal size={16} /></IconTool>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <button className="mb-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-slate-950 px-3 text-sm font-medium text-white"><Plus size={15} /> {t('pages.newTopic')}</button>
              <button onClick={() => setActiveTopic('All topics')} className={`mb-1 w-full rounded-md px-3 py-2 text-left text-sm font-medium ${activeTopic === 'All topics' ? 'bg-slate-100 text-slate-950' : 'text-slate-600 hover:bg-slate-50'}`}>{t('pages.allTopics')}</button>
              {topics.map((topic) => (
                <button key={topic.id} onClick={() => setActiveTopic(topic.name)} className={`mb-1 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm ${activeTopic === topic.name ? 'bg-slate-100 font-medium text-slate-950' : 'text-slate-600 hover:bg-slate-50'}`}>
                  <span className="truncate">{topic.name}</span><span className="text-xs text-slate-400">{topic.prompt_count}</span>
                </button>
              ))}
            </div>
          </aside>

          <SectionCard className="flex min-h-0 min-w-0 flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
              <MiniTabs items={tabItems} active={tabItems[activeTabIdx]} onChange={(item) => setActiveTabIdx(tabItems.indexOf(item))} />
              <div className="flex items-center gap-2"><ToolButton label={`${visiblePrompts.length} / 50`} /><button className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-950 px-3 text-sm font-medium text-white"><Plus size={15} /> {t('pages.addPrompt')}</button></div>
            </div>
            <SearchToolbar>
              <span className="hidden text-sm text-slate-500 md:inline">{t('pages.visibility')} <Delta /> {t('pages.sentiment')} <Delta /> {t('pages.position')} <Delta /></span>
              <ToolbarIcons />
            </SearchToolbar>
            <TableFrame className="min-h-0 flex-1">
              <thead>
                <tr>
                  <Th align="left" className="sticky left-0 z-20 bg-zinc-50">{t('pages.prompt')}</Th>
                  <Th>{t('pages.visibility')}</Th><Th>{t('pages.sentiment')}</Th><Th>{t('pages.position')}</Th><Th>{t('pages.mentions')}</Th>
                  <Th>{t('pages.volume')} <Badge tone="blue">{t('pages.beta')}</Badge></Th><Th>{t('pages.tags')}</Th><Th>{t('pages.location')}</Th>
                  <Th>{t('pages.sov')}</Th><Th>{t('pages.webSearch')}</Th><Th>{t('pages.added')}</Th>
                </tr>
              </thead>
              <tbody>
                {visiblePrompts.map((prompt) => (
                  <tr key={prompt.id} className="group hover:bg-slate-50">
                    <Td align="left" className="sticky left-0 z-10 bg-white transition-colors group-hover:bg-slate-50">
                      <div className="max-w-xs"><div className="font-medium text-slate-950">{prompt.title}</div><div className="line-clamp-1 text-xs text-slate-500">{prompt.description}</div></div>
                    </Td>
                    <Td><span className="font-semibold">{prompt.visibility ?? 0}%</span><Delta /></Td>
                    <Td>{prompt.sentiment == null ? <span className="text-slate-300">-</span> : prompt.sentiment}</Td>
                    <Td>{prompt.position == null ? <span className="text-slate-300">-</span> : `# ${prompt.position}`}</Td>
                    <Td>{prompt.mentions ?? 0}</Td>
                    <Td>{prompt.volume?.toLocaleString() ?? '-'}</Td>
                    <Td><div className="flex flex-wrap gap-1">{prompt.tags?.map((tag) => <Badge key={tag}>{tag}</Badge>)}</div></Td>
                    <Td>{prompt.location || '-'}</Td>
                    <Td>{prompt.sov ?? 0}%</Td>
                    <Td>{prompt.web_search ? t('pages.on') : t('pages.off')}</Td>
                    <Td>{new Date(prompt.created_at).toLocaleDateString('zh-CN')}</Td>
                  </tr>
                ))}
              </tbody>
            </TableFrame>
            <div className="px-4 py-3 text-sm text-slate-500">{visiblePrompts.length} {t('pages.prompts')}</div>
          </SectionCard>
        </div>
      </div>
    </main>
  );
};

export default PromptsPage;
